import React, { useRef, useEffect } from 'react';
import LiveOrderBook from './LiveOrderBook';
import { fmtUsd } from '../utils/fmtPrice';
import {
  TRADE_ACTION_OPTIONS,
  sideTabsForAction,
  normalizeTradeAction,
  positionStatusLabel,
} from '../utils/tradeActionUi';
import './MultiAssetTradePanel.css';

export function useTradePanelScroll() {
  const panelRef = useRef(null);
  const scrollToPanel = () => {
    panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return { panelRef, scrollToPanel };
}

function leverageOptions(maxLev) {
  const cap = Math.max(1, Math.min(50, Math.floor(Number(maxLev) || 1)));
  return Array.from({ length: cap }, (_, i) => i + 1);
}

export default function MultiAssetTradePanel({
  panelRef,
  form,
  setForm,
  symbols,
  submitting,
  onSubmit,
  selectedPosition,
  onClosePositionQuick,
  closeQuickBusy,
  tradingMode = 'manual',
  leverageCap = 10,
  hlMaxLeverage = null,
  embedOrderBook = true,
  introText,
  strategyLeverage = 2,
  prefillSignalId = null,
  paperMode = false,
  submitLabel,
}) {
  useEffect(() => {
    if (selectedPosition && panelRef?.current) {
      panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedPosition?.symbol, selectedPosition?.side, panelRef]);

  const action = normalizeTradeAction(form.action);
  const sideTabs = sideTabsForAction(action);
  const deploy = Number(form.sizeUsd) || 0;
  const lev = Math.max(1, Number(form.leverage) || 1);
  const notional = deploy * lev;
  const levChoices = leverageOptions(leverageCap);

  useEffect(() => {
    if (lev > leverageCap) {
      setForm((f) => ({ ...f, leverage: leverageCap }));
    }
  }, [leverageCap, lev, setForm]);

  useEffect(() => {
    if (form.action && normalizeTradeAction(form.action) !== form.action) {
      setForm((f) => ({ ...f, action: normalizeTradeAction(f.action) }));
    }
  }, [form.action, setForm]);

  const setAction = (nextAction) => {
    setForm((f) => ({ ...f, action: nextAction }));
  };

  const setSide = (nextSide) => {
    setForm((f) => ({ ...f, side: nextSide }));
  };

  return (
    <div ref={panelRef} id="trade-panel" className="multi-asset-trade-panel">
      {selectedPosition ? (
        <div className="trade-panel-banner">
          <span>
            <strong>{selectedPosition.symbol}</strong> —{' '}
            {selectedPosition.positionAction || positionStatusLabel(selectedPosition.side)} (
            {fmtUsd(selectedPosition.marginUsd ?? selectedPosition.sizeUsd)}
            {selectedPosition.isPaper ? ', paper' : ', live'})
          </span>
          <span className="trade-panel-banner-hint">
            Form preset to close this lot — use Sell to exit long, Cover to exit short.
          </span>
        </div>
      ) : prefillSignalId ? (
        <p className="trade-panel-intro">
          Prefilled from MAP signal #{prefillSignalId} — margin sized to your wallet at the MAP book %. Stop/target
          filled when MAP provides them. Adjust before queuing.
        </p>
      ) : (
        <p className="trade-panel-intro">
          {introText || 'Select a symbol below — the live book loads in the next section.'}
        </p>
      )}

      {embedOrderBook && (
        <LiveOrderBook
          symbol={form.symbol}
          hlDex={form.hlDex || 'xyz'}
          hlCoin={form.hlCoin}
          titlePrefix="Live book"
        />
      )}

      <form className="manual-form trade-panel-form" onSubmit={onSubmit}>
        <div className="form-row">
          <label>Symbol</label>
          <select value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })}>
            {symbols.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {hlMaxLeverage != null && (
            <span className="trade-panel-hl-max">Hyperliquid max leverage: {hlMaxLeverage}x</span>
          )}
        </div>

        <div className="trade-action-tabs">
          {TRADE_ACTION_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={action === opt.value ? 'active' : ''}
              onClick={() => setAction(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="trade-side-tabs">
          {sideTabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className={`${tab.value} ${form.side === tab.value ? 'active' : ''}`}
              onClick={() => setSide(tab.value)}
              title={tab.hint}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {action === 'close' && (
          <p className="trade-action-hint">
            Closing uses <strong>Sell</strong> to exit a long and <strong>Cover</strong> to exit a short (reduce-only).
          </p>
        )}
        {action === 'add' && (
          <p className="trade-action-hint">
            Add increases an existing position in the same direction (long add or short add).
          </p>
        )}

        <div className="form-row two">
          <div>
            <label>{tradingMode === 'manual' ? 'Deploy (USD margin, suggested)' : 'Deploy (USD margin)'}</label>
            <input
              type="number"
              min={5}
              step={1}
              value={form.sizeUsd}
              onChange={(e) => setForm({ ...form, sizeUsd: e.target.value })}
              required
            />
          </div>
          <div>
            <label>
              Leverage ({tradingMode === 'obot' ? `max ${leverageCap}x admin cap` : `max ${leverageCap}x on HL`})
            </label>
            <select
              value={String(form.leverage || 1)}
              onChange={(e) => setForm({ ...form, leverage: Number(e.target.value) })}
            >
              {levChoices.map((x) => (
                <option key={x} value={x}>
                  {x}x
                </option>
              ))}
            </select>
          </div>
        </div>
        {deploy > 0 && (
          <p className="trade-panel-notional-hint">
            Notional ≈ {fmtUsd(notional)} ({lev}x × {fmtUsd(deploy)} margin)
          </p>
        )}
        {tradingMode === 'obot' && (
          <p className="trade-panel-strategy-hint">
            Omni MAP strategy signals still run at {strategyLeverage}x automatically. This leverage applies to trades
            you queue from this page.
          </p>
        )}
        <div className="form-row two">
          <div>
            <label>Stop (optional)</label>
            <input
              type="number"
              value={form.stopPrice}
              onChange={(e) => setForm({ ...form, stopPrice: e.target.value })}
              placeholder="e.g. 4200 for GOLD"
            />
          </div>
          <div>
            <label>Target (optional)</label>
            <input
              type="number"
              value={form.targetPrice}
              onChange={(e) => setForm({ ...form, targetPrice: e.target.value })}
            />
          </div>
        </div>
        <div className="form-row">
          <label>Reason / note</label>
          <input
            type="text"
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            placeholder="e.g. Close gold tranche"
          />
        </div>
        <div className="manual-form-actions">
          <button type="submit" className="primary-btn" disabled={submitting}>
            {submitting
              ? 'Submitting…'
              : submitLabel || (paperMode ? 'Review paper trade' : 'Queue trade signal')}
          </button>
          {selectedPosition && action === 'close' && onClosePositionQuick && (
            <button
              type="button"
              className="danger-btn"
              disabled={closeQuickBusy || submitting}
              onClick={() => onClosePositionQuick(selectedPosition)}
            >
              {closeQuickBusy ? 'Closing…' : `Close ${selectedPosition.side} ${selectedPosition.symbol}`}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
